
export interface ChartDataPoint {
  [key: string]: any;
}

export interface ChartConfig {
  xField?: string;
  yField?: string;
  colorField?: string;
  chartType: 'bar' | 'line' | 'pie' | 'scatter' | 'area' | 'histogram' | 'table';
}

/**
 * Transform raw data into Vega-compatible format
 */
export const transformDataForVega = (
  data: any[],
  config: ChartConfig
): ChartDataPoint[] => {
  if (!data || data.length === 0) {
    return [];
  }

  // If data is already in the right format, return as is
  if (Array.isArray(data) && typeof data[0] === 'object') {
    return data;
  }

  // Handle different data formats
  if (Array.isArray(data)) {
    // If it's a simple array of numbers, create objects with default fields
    if (typeof data[0] === 'number') {
      return data.map((value, index) => ({
        index,
        value,
        category: `Item ${index + 1}`
      }));
    }

    // If it's an array of arrays, assume first row is headers
    if (Array.isArray(data[0])) {
      const headers = data[0];
      return data.slice(1).map((row, index) => {
        const obj: ChartDataPoint = {};
        headers.forEach((header: string, i: number) => {
          obj[header] = row[i];
        });
        return obj;
      });
    }
  }

  // If it's an object with series data
  if (typeof data === 'object' && !Array.isArray(data)) {
    const transformed: ChartDataPoint[] = [];
    Object.keys(data).forEach(key => {
      if (Array.isArray(data[key])) {
        (data[key] as any[]).forEach((value: any, index: number) => {
          transformed.push({
            category: key,
            value,
            index
          });
        });
      }
    });
    return transformed;
  }

  return data;
};

/**
 * Detect chart type from data structure
 */
export const detectChartType = (data: any[]): string => {
  if (!data || data.length === 0) return 'bar';
  
  const firstItem = data[0];
  
  if (firstItem.x !== undefined && firstItem.y !== undefined) {
    return 'scatter';
  }
  
  if (firstItem.category !== undefined && firstItem.value !== undefined) {
    return 'bar';
  }
  
  if (firstItem.date !== undefined || firstItem.time !== undefined) {
    return 'line';
  }
  
  return 'bar';
};

/**
 * Extract field names from data
 */
export const extractFields = (data: ChartDataPoint[]): {
  xField?: string;
  yField?: string;
  colorField?: string;
} => {
  if (!data || data.length === 0) {
    return {};
  }
  
  const firstItem = data[0];
  const fields = Object.keys(firstItem);
  
  // Common field name patterns
  const xField = fields.find(f => 
    ['x', 'category', 'name', 'label', 'date', 'time'].includes(f.toLowerCase())
  );
  
  const yField = fields.find(f => 
    ['y', 'value', 'count', 'amount', 'quantity'].includes(f.toLowerCase())
  );
  
  const colorField = fields.find(f => 
    ['color', 'group', 'type', 'category'].includes(f.toLowerCase()) && f !== xField
  );
  
  return {
    xField: xField || fields[0],
    yField: yField || fields[1],
    colorField: colorField || undefined
  };
};
